import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, ShoppingBag, User, Wallet, LogOut, Menu, X } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Header() {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavLinks = ({ onClick }) => (
    <>
      <Link to="/" onClick={onClick} data-testid="nav-home" className="hover-underline text-sm tracking-wide">
        {t("home")}
      </Link>
      <Link to="/shop" onClick={onClick} data-testid="nav-shop" className="hover-underline text-sm tracking-wide">
        {t("shop")}
      </Link>
      <Link to="/shop?on_offer=true" onClick={onClick} data-testid="nav-offers" className="hover-underline text-sm tracking-wide">
        {t("onOffer")}
      </Link>
    </>
  );

  return (
    <header
      className="sticky top-0 z-40 border-b border-[#E5E1D8] backdrop-blur-xl bg-[#FAF9F6]/85"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <img
            src="/nebula-logo.jpg"
            alt="Nebula Prestige"
            className="h-8 w-8 rounded-md object-cover"
          />
          <span className="font-display text-xl tracking-tight brand-gradient-text">{t("brand")}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10 text-[#1A1738]">
          <NavLinks />
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E1D8] text-xs uppercase tracking-widest hover:bg-[#F3F0EB] transition-colors"
          >
            <Globe className="h-3.5 w-3.5" strokeWidth={1.5} />
            {lang === "en" ? "AR" : "EN"}
          </button>

          {user && (
            <Link
              to="/wallet"
              data-testid="wallet-quick"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A1738] text-[#FAF9F6] text-xs font-medium"
            >
              <Wallet className="h-3.5 w-3.5" strokeWidth={1.5} />
              ${user.wallet_balance.toFixed(2)}
            </Link>
          )}

          <Link
            to="/cart"
            data-testid="cart-link"
            className="relative p-2 rounded-full hover:bg-[#F3F0EB] transition-colors"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            {cart.count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#7B5CBF] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                {cart.count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu" className="p-2 rounded-full hover:bg-[#F3F0EB] transition-colors">
                  <User className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <div className="px-2 py-1.5 text-xs text-[#767676]">{user.phone || user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet">
                  <Wallet className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("wallet")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  {t("orders")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    {t("admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-signout">
                  <LogOut className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/login")}
              data-testid="signin-btn"
              className="hidden md:inline-flex"
            >
              {t("signIn")}
            </Button>
          )}

          <button onClick={() => setMobileOpen((v) => !v)} className="md:hidden p-2" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E1D8] bg-[#FAF9F6]">
          <div className="px-6 py-4 flex flex-col gap-4">
            <NavLinks onClick={() => setMobileOpen(false)} />
            <button
              onClick={() => { toggle(); setMobileOpen(false); }}
              className="text-sm text-start"
              data-testid="mobile-lang-toggle"
            >
              {lang === "en" ? "العربية" : "English"}
            </button>
            {!user && (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm" data-testid="mobile-signin">
                {t("signIn")}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
